import React from "react";
import { Sparkles, Bot, Zap, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const seekUrl = (u) => u ? (typeof u === 'string' && u.includes('#') ? u : u + '#t=0.5') : u;


const CreativeWorkspace = React.memo(() => {
  const navigate = useNavigate();
  
  return (
    <section className="py-24 bg-secondary/30" data-testid="workflow-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-[hsl(var(--accent))]" />
            <h2 className="text-4xl sm:text-5xl font-bold" data-testid="workflow-title">
              KI Lösungen für dein Business
            </h2>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Von KI-gestütztem Marketing-Content mit Freycreate.ai bis zur vollständigen Prozessautomatisierung im Unternehmen – moderne KI-Tools für moderne Content-Erstellung
          </p>
        </div>

        {/* Main Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          
          {/* Freycreate.ai */}
          <div className="bg-background/50 backdrop-blur-sm rounded-2xl p-8 border border-[hsl(var(--accent))]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Freycreate.ai</h3>
                <p className="text-sm text-muted-foreground">All-in-One KI-Kreativstudio (Beta)</p>
              </div>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              Meine eigene <span className="text-[hsl(var(--accent))] font-semibold">KI-Plattform für Marketing-Content</span> – erstelle AI Characters, integriere Produkte und generiere maßgeschneiderte Inhalte für dein Unternehmen. Von Social Ads bis zu kompletten Werbekampagnen – alles aus einer Hand, ohne Tool-Hopping.
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground"><strong>Alles aus einer Hand:</strong> Image Gen, Video Gen, Characters, Logos, Ads</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground"><strong>Top AI-Modelle:</strong> Gemini Nano Banana, Veo 3.1, Kling 2.6, Flux 2.0</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground"><strong>Brand-fokussiert:</strong> Ad Builder, Character Creator, Logo Generator</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground"><strong>Ein Credit-System</strong> statt 10+ verschiedene Abos</span>
              </div>
            </div>

            {/* Freycreate Screenshots Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_insta-integrator/artifacts/4chvra9g_Bildschirmfoto%202025-12-14%20um%2017.37.03.png"
                alt="Freycreate.ai Dashboard - Brand Studio"
                className="w-full rounded-lg border border-[hsl(var(--accent))]/20"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_insta-integrator/artifacts/ielf6lad_Bildschirmfoto%202026-01-07%20um%2023.39.15.png"
                alt="Freycreate.ai Image Generator - Nano Banana Pro"
                className="w-full rounded-lg border border-[hsl(var(--accent))]/20"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_insta-integrator/artifacts/vy1o90lu_Bildschirmfoto%202026-01-07%20um%2023.43.03.png"
                alt="Freycreate.ai Logo Builder"
                className="w-full rounded-lg border border-[hsl(var(--accent))]/20"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_insta-integrator/artifacts/4n32kteg_Bildschirmfoto%202026-01-07%20um%2023.45.37.png"
                alt="Freycreate.ai Character Hub"
                className="w-full rounded-lg border border-[hsl(var(--accent))]/20"
              />
            </div>

            <a
              href="https://freycreate.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white rounded-xl font-medium transition-all neon-glow"
            >
              Freycreate.ai entdecken
              <Sparkles className="w-4 h-4" />
            </a>
          </div>

          {/* Freys Media KI Agenten */}
          <div className="bg-background/50 backdrop-blur-sm rounded-2xl p-8 border border-[hsl(var(--accent))]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Freys Media KI Agenten</h3>
                <p className="text-sm text-muted-foreground">Business Process Automation</p>
              </div>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              Integration von <span className="text-[hsl(var(--accent))] font-semibold">KI Agenten in dein Unternehmen</span> zur Automatisierung von Prozessen. Von Kundenkommunikation bis zu internen Workflows – effizient, intelligent, zukunftssicher.
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground">Automatisierung von Unternehmensprozessen</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground">KI-gestützte Kundenkommunikation</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]"></div>
                </div>
                <span className="text-sm text-muted-foreground">Maßgeschneiderte Integration in bestehende Systeme</span>
              </div>
            </div>

            <button className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white rounded-xl font-medium transition-all">
              Mehr erfahren
              <Bot className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video Demo Section */}
        <div className="bg-background/50 rounded-2xl p-8 md:p-12 border border-[hsl(var(--accent))]/20">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Zap className="w-6 h-6 text-[hsl(var(--accent))]" />
              <h3 className="text-2xl font-bold">Freycreate in Aktion</h3>
            </div>
            <p className="text-muted-foreground">
              Beispiel einer Product-Werbung, erstellt mit Freycreate – von der Idee zum fertigen Video in Minuten
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="aspect-[9/16] bg-secondary rounded-xl overflow-hidden mx-auto max-w-xs">
              <video controls preload="metadata" className="w-full h-full object-cover">
                <source src={seekUrl("https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/product-ad.mp4")} type="video/mp4" />
              </video>
            </div>
            <div className="aspect-[9/16] bg-secondary rounded-xl overflow-hidden mx-auto max-w-xs">
              <video controls preload="metadata" className="w-full h-full object-cover">
                <source src={seekUrl("https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/freepik-dynamic-closeup-shot-a-woman-in-a-stylish-black-le-95582.mp4")} type="video/mp4" />
              </video>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Von Story-Konzept über Character-Design bis zum finalen Video – alles auf einer Plattform
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <span className="px-4 py-2 bg-secondary/50 rounded-full text-sm">AI Characters</span>
              <span className="px-4 py-2 bg-secondary/50 rounded-full text-sm">Produkt-Integration</span>
              <span className="px-4 py-2 bg-secondary/50 rounded-full text-sm">Video-Generierung</span>
              <span className="px-4 py-2 bg-secondary/50 rounded-full text-sm">Social-Ready</span>
              <span className="px-4 py-2 bg-secondary/50 rounded-full text-sm">KI Workshops</span>
            </div>
            
            {/* CTA Button */}
            <button
              onClick={() => navigate('/service/ki-video-generation')}
              className="px-8 py-4 bg-gradient-to-r from-[hsl(var(--accent))] to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity font-semibold shadow-lg hover:shadow-xl"
            >
              Mehr über KI Video Generation →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
});

export default CreativeWorkspace;
